import { useDispatch, useSelector } from 'react-redux';
import { TextInput } from '@interstate/components/TextInput';
import { PhoneInput } from '@interstate/components/PhoneInput';
import { Typography } from '@interstate/components/Typography';
import { RevealableMaskTextInput } from '@makemydeal/dr-shared-components';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NameFieldsWrapper } from '../CustomerCard.styles';
import { useCustomerCardContext } from '../CustomerCardProvider';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import {
    BANK_NAME_FIELD,
    BANK_ACCOUNT_NUMBER_FIELD,
    BANK_CONTACT_NAME_FIELD,
    BANK_CONTACT_PHONE_FIELD,
    CHECKING_ACCOUNT_NUMBER_MASK
} from '../constants';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';

export const BankInformation = () => {
    const dispatch = useDispatch();
    const { formValues, handleInputChange, isLocked, customerFormErrors, customerValidator } = useCustomerCardContext();
    const canDesk = useSelector((state: StateTree) => customerSpdSelectors.getHasDeskingPermission(state));
    const businessErrors = customerFormErrors.business as Record<string, ErrorState> | undefined;

    const commonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
    const checkSpdData = useSelector(customerSpdSelectors.getCheckCustomerSpdResult(commonConsumerId));
    const checkSpdError = useSelector(customerSpdSelectors.getCheckCustomerSpdError());
    const isGetLoading = useSelector(customerSpdSelectors.getCustomerSpdLoading());
    const getCustomerDataSuccess = useSelector((state: StateTree) =>
        customerSpdSelectors.getCustomerAPISuccess(state, commonConsumerId)
    );
    const bankAccountNumber = formValues.bankAccountNumber;
    const showBankAccountNumber = Boolean(commonConsumerId && !checkSpdError);

    const handleBankPhoneChange = (e: InterstateOnChangeEvent<string>) => {
        customerValidator.setValue(BANK_CONTACT_PHONE_FIELD, e.target.value, { validate: false });
        const isValid = e.isValid as boolean;
        customerValidator.setError(BANK_CONTACT_PHONE_FIELD, isValid, isValid ? '' : 'Bank Phone is invalid');
    };

    return (
        <div data-testid="bank-information-section">
            <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }} data-testid="bank-information-header">
                Bank Information
            </Typography>
            <NameFieldsWrapper columns={4}>
                <TextInput
                    name="business-bankName"
                    displayLabel={true}
                    label="Bank Name"
                    value={formValues.business?.bankName || ''}
                    onChange={(e) => handleInputChange(BANK_NAME_FIELD, e.target.value)}
                    data-testid="business-info-bank-name"
                    size="small"
                    disabled={isLocked}
                    hasError={Boolean(businessErrors?.bankName?.hasError)}
                    errorMessage={businessErrors?.bankName?.errorMessage || ''}
                />
                <TextInput
                    name="business-bankContactName"
                    displayLabel={true}
                    label="Bank Contact Name"
                    value={formValues.business?.bankContactName || ''}
                    onChange={(e) => handleInputChange(BANK_CONTACT_NAME_FIELD, e.target.value)}
                    data-testid="business-info-bank-contact-name"
                    size="small"
                    disabled={isLocked}
                    hasError={Boolean(businessErrors?.bankContactName?.hasError)}
                    errorMessage={businessErrors?.bankContactName?.errorMessage || ''}
                />
                <PhoneInput
                    name="business-bankContactPhoneNumber"
                    label="Bank Phone"
                    value={formValues.business?.bankContactPhoneNumber || ''}
                    onChange={handleBankPhoneChange}
                    data-testid="business-info-bank-phone"
                    size="sm"
                    disabled={isLocked}
                />
                {showBankAccountNumber && (
                    <RevealableMaskTextInput
                        name="business-checkingAccountNumber"
                        label="Checking Account Number"
                        data-testid="business-info-checking-account-number"
                        maskedValue={CHECKING_ACCOUNT_NUMBER_MASK}
                        onChange={(_field, value) => handleInputChange(BANK_ACCOUNT_NUMBER_FIELD, value)}
                        value={bankAccountNumber || ''}
                        defaultMask={!!(checkSpdData?.bankAccountNumber || bankAccountNumber)}
                        disabled={isLocked}
                        hasError={Boolean((customerFormErrors.bankAccountNumber as ErrorState | undefined)?.hasError)}
                        errorMessage={(customerFormErrors.bankAccountNumber as ErrorState | undefined)?.errorMessage || ''}
                        loading={isGetLoading}
                        canReveal={canDesk}
                        onToggle={(isVisible) => {
                            if (isVisible && !getCustomerDataSuccess) {
                                dispatch(customerSpdActionCreators.customerSpdRSAA(commonConsumerId));
                            }
                        }}
                    />
                )}
            </NameFieldsWrapper>
        </div>
    );
};
